
import { Component, Emit, Prop } from 'vue-property-decorator';
import { Table } from '@/interfaces/models/Table';
import { OrderFlavor } from '@/enums/OrderFlovor';
import { OrderStatus } from '@/enums/OrderStatus';
import { OrderType } from '@/enums/OrderType';
import { Permission } from '@/enums/Permission';
import { VenueLabel } from '@/interfaces/models/VenueLabel';
import CustomerGroup from '@/mixins/CustomerGroup';
import { mixins } from 'vue-class-component';
import VColumnSelect from '@/components/shared/table/VColumnSelect.vue';
import VRangePickerOneField from '@/components/shared/form/VDateRangePickerOneField.vue';
import { Range } from '@/interfaces/components/Range';
import moment from 'moment';
import { ExternalOrderProvider } from '@/enums/ExternalOrderProvider';
import { CheckoutIntegrationStatus } from '@/enums/CheckoutIntegrationStatus';
import { PaymentMethod } from '@/enums/PaymentMethod';
import { OrderPlatformType as Platform } from '@/enums/OrderPlatformType';
import { can } from '@/util/helper';

interface Selection {
  ids: string;
  flavors: OrderFlavor[];
  types: OrderType[];
  status: OrderStatus[];
  tables: Table[];
}

@Component({
  components: { VRangePickerOneField, VColumnSelect },
})
export default class StatisticFilter extends mixins(CustomerGroup) {
  @Prop({ type: Array, default: () => [] }) public tables!: Table[];
  @Prop({ type: Array, default: () => [] }) public labels!: VenueLabel[];
  @Prop({ type: Array, default: () => [] }) public sourceHeaders!: any[];

  public ids = '';
  public code = '';
  public selectedFlavors: OrderFlavor[] = [];
  public selectedTypes: OrderType[] = [];
  public selectedStatus: OrderStatus[] = [];
  public selectedPlatforms: Platform[] = [];
  public selectedCheckoutIntegrationStatus: CheckoutIntegrationStatus[] = [];
  public selectedPaymentMethod: PaymentMethod[] = [];
  public selectedTables: string[] = [];
  public selectedLabel: string = '';
  public selectedCustomerGroup: string = '';
  public selectedExternalOrderProvider: string = '';
  public selectedSmoothrOnly: boolean = false;
  public range: Range = {
    from: '',
    to: '',
  };

  public timeout: any;
  public timeoutCodeInput: any;

  public handledFilterDay: string = '';

  get selection() {
    return {
      ids: this.ids,
      flavor: this.selectedFlavors,
      type: this.selectedTypes,
      status: this.selectedStatus,
      platform: this.selectedPlatforms,
      table: this.selectedTables,
      venueLabel: this.selectedLabel,
      customerGroup: this.selectedCustomerGroup,
      externalOrderProvider: this.selectedExternalOrderProvider,
      code: this.code.length > 0 ? [this.code] : undefined,
      ...this.range,
      ...(this.$isAdmin()
        ? {
            checkoutIntegrationStatus: this.selectedCheckoutIntegrationStatus,
            method: this.selectedPaymentMethod,
            ...(this.selectedSmoothrOnly ? { smoothrOnly: this.selectedSmoothrOnly } : {}),
          }
        : {}),
    };
  }

  public mounted(): void {
    this.$nextTick(() => {
      this.applyDay('today');
      this.selectedFlavors = this.defaultFlavors;
      this.selectedStatus = this.defaultStatus;
    });
  }

  get defaultFlavors() {
    if (this.$can(Permission.STATISTICS_FLAVOR)) {
      return [];
    }

    return [OrderFlavor.PROD_RELEASE];
  }

  get defaultStatus() {
    if (this.$isAdmin()) {
      return [];
    }
    const accessibleStatus = [OrderStatus.IN_PREPARATION, OrderStatus.DONE];
    const allStatus = Object.values(OrderStatus);
    for (const status of allStatus) {
      if (this.$can(`order.filter.status.${status}` as Permission) && !accessibleStatus.includes(status)) {
        accessibleStatus.push(status);
      }
    }
    return accessibleStatus;
  }

  get flavors(): string[] {
    return Object.values(OrderFlavor);
  }

  get status() {
    const allStatuses = Object.values(OrderStatus).map((status) => ({
      value: status,
      text: this.$t(`orderStatus.${status}`),
    }));

    if (this.$isAdmin()) {
      return allStatuses;
    }

    return allStatuses.filter(({ value }) => {
        if ([OrderStatus.IN_PREPARATION, OrderStatus.DONE].includes(value as OrderStatus)) return true;
        return this.$can(`order.filter.status.${value}` as Permission);
      }
    );
  }

  get checkoutIntegrationStatus() {
    if (this.$can(Permission.ORDER_FILTER_CHECKOUT_INTEGRATION_STATUS) || this.$isAdmin) {
      return Object.values(CheckoutIntegrationStatus).map((status) => ({
        value: status,
        text: this.$t(`checkoutIntegrationStatus.${status}`),
      }));
    }

    return [];
  }

  get paymentMethod() {
    if (!this.$isAdmin()) {
      return [];
    }

    return Object.values(PaymentMethod).map((method) => ({
      value: method,
      text: this.$t(`paymentTypes.${method}`),
    }));
  }

  get types(): string[] {
    if (this.$isAdmin()) {
      return Object.values(OrderType);
    }
    return [OrderType.STANDARD, OrderType.PREORDER, OrderType.TERMINAL];
  }

  get externalOrderProviders() {
    return [
      { value: ExternalOrderProvider.JET, text: 'Jet' },
      { value: ExternalOrderProvider.WOLT, text: 'Wolt' },
      { value: ExternalOrderProvider.UBER_EATS, text: 'Uber Eats' },
      { value: ExternalOrderProvider.DELIVEROO, text: 'Deliveroo' },
    ];
  }

  get platforms() {
    return Object.values(Platform).map((platform) => ({
      value: platform,
      text: this.$t(`stats.platforms.${platform}`),
    }));
  }

  public onChangeRange(event: { from: string; to: string }) {
    this.range.from = event.from;
    this.range.to = event.to;
  }

  public applyDay(day: 'today' | 'yesterday') {
    if (day === 'today') {
      this.range.from = moment().subtract(0, 'day').format('YYYY-MM-DD');
      this.range.to = moment().format('YYYY-MM-DD');
    } else if (day === 'yesterday') {
      this.range.from = moment().subtract(1, 'day').format('YYYY-MM-DD');
      this.range.to = moment().subtract(1, 'day').format('YYYY-MM-DD');
    }
    this.$emit('change', this.selection);
    this.handledFilterDay = day;
  }

  public applySelectedFilters() {
    this.$emit('change', this.selection);
  }

  @Emit('change')
  public reset() {
    this.ids = '';
    this.code = '';
    this.selectedFlavors = this.defaultFlavors;
    this.selectedTypes = [];
    this.selectedStatus = [];
    this.selectedPlatforms = [];
    this.selectedCheckoutIntegrationStatus = [];
    this.selectedPaymentMethod = [];
    this.selectedTables = [];
    this.selectedLabel = '';
    this.selectedCustomerGroup = '';
    this.selectedExternalOrderProvider = '';
    this.handledFilterDay = '';
    this.selectedSmoothrOnly = false;
    this.range = {
      from: '',
      to: '',
    };

    return this.selection;
  }
}
